import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  INSURANCE_POLICIES_LIST_QUERY_KEY,
  createInsurancePolicyDetailQueryKey,
  createInsurancePolicyFileQueryKey,
  deleteInsurancePolicy,
  fetchInsurancePolicies,
  fetchInsurancePolicyById,
  fetchInsurancePolicyDocumentFile,
  uploadInsurancePoliciesBatch,
} from "@/api/insurancePolicies";
import { ANALYZING_REFETCH_INTERVAL_MS } from "@/features/cle-compliance/hooks/analyzingRefetch";
import { invalidateInsurancePolicyQueries } from "@/features/cle-compliance/hooks/invalidateCleDocumentQueries";
import type { PaginatedResponse } from "@/features/cle-compliance/models/courseCertificate";
import type {
  InsurancePolicyDocumentModel,
  InsurancePolicyQueryParams,
  InsurancePolicyUploadBatchResponse,
} from "@/features/cle-compliance/models/insurancePolicy";
import { isPolicyAnalyzingStatus } from "@/features/cle-compliance/models/policyStatus";

export {
  INSURANCE_POLICIES_LIST_QUERY_KEY,
  INSURANCE_POLICIES_QUERY_KEY,
  createInsurancePolicyDetailQueryKey,
  createInsurancePolicyFileQueryKey,
} from "@/api/insurancePolicies";

export const useInsurancePolicies = (params: InsurancePolicyQueryParams = {}) =>
  useQuery<PaginatedResponse<InsurancePolicyDocumentModel>>({
    queryKey: [...INSURANCE_POLICIES_LIST_QUERY_KEY, params],
    queryFn: () => fetchInsurancePolicies(params),
    staleTime: 5 * 60 * 1000,
  });

export const useInsurancePolicyDocument = (id: string) =>
  useQuery<InsurancePolicyDocumentModel>({
    enabled: id.length > 0,
    queryKey: createInsurancePolicyDetailQueryKey(id),
    queryFn: () => fetchInsurancePolicyById(id),
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data && isPolicyAnalyzingStatus(query.state.data.status)
        ? ANALYZING_REFETCH_INTERVAL_MS
        : false,
  });

export const useInsurancePolicyDocumentFile = (id?: string) =>
  useQuery<Blob>({
    enabled: !!id,
    queryKey: createInsurancePolicyFileQueryKey(id ?? ""),
    queryFn: () => fetchInsurancePolicyDocumentFile(id!),
    staleTime: 5 * 60 * 1000,
  });

export const useUploadInsurancePoliciesBatch = () => {
  const queryClient = useQueryClient();

  return useMutation<InsurancePolicyUploadBatchResponse, Error, File[]>({
    mutationFn: uploadInsurancePoliciesBatch,
    onSuccess: () => {
      invalidateInsurancePolicyQueries(queryClient);
    },
  });
};

export const useDeleteInsurancePolicy = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteInsurancePolicy,
    onSuccess: (_data, id) => {
      invalidateInsurancePolicyQueries(queryClient, id);
    },
  });
};
